import { useQuery } from "@tanstack/react-query";
import { useNavigate, useParams } from "react-router";

import { isDocumentUploaded } from "@/components/DocumentInput/useDocumentInput";
import { ErrorPage } from "@/components/Error/ErrorPage";
import { FullSizeLoader } from "@/design-system/FullSizeLoader";
import { getOriginalTranslationValue } from "@/helpers/languages";
import { commonAPIDataSelector } from "@/helpers/Network/selectors";
import { useSlug } from "@/hooks/useSlug";
import { documentMutations } from "@/queries/documents/mutations";
import { useDocumentQueries } from "@/queries/documents/queryOptions";
import { fileMutations } from "@/queries/files/mutations";
import { routes } from "@/routes";

import { DocumentFileForm, type DocumentFileFormProps, type DocumentFileFormValues } from "./DocumentFileForm";

export function EditDocumentFilePage(): React.ReactNode {
  const slug = useSlug();
  const navigate = useNavigate();
  const { folderId, fileId } = useParams<{ folderId: string; fileId: string }>();

  const documentQueries = useDocumentQueries();
  const {
    data: fileDetails,
    isFetching: fileDetailsIsFetching,
    error: fileDetailsError,
  } = useQuery({
    ...documentQueries.fileDetails({
      folderId: folderId!,
      fileId: fileId!,
    }),
    enabled: fileId != null,
  });
  const {
    data: parentFolder,
    isPending: parentFolderIsPending,
    error: parentFolderError,
  } = useQuery({
    ...documentQueries.listDocuments({
      folderId: folderId!,
    }),
    enabled: !!folderId,
    select: (res) => {
      const data = commonAPIDataSelector(res);

      return {
        id: data.id,
        name: data.name,
      };
    },
  });

  const createDocumentMutation = fileMutations.useAddDocument();
  const updateFileMutation = documentMutations.useUpdateFile(fileId!, folderId!);

  async function edit(data: DocumentFileFormValues) {
    const upload = await uploadDocumentIfNotUploaded(data);

    await updateFileMutation.mutateAsync(
      {
        name: data.name,
        nameTranslations: [],
        parentFolderId: folderId!,
        uploadId: upload.id,
      },
      {
        onSuccess: () => navigate(routes.documents.documentsList({ slug, folderId: folderId! })),
      },
    );
  }

  async function uploadDocumentIfNotUploaded(data: DocumentFileFormValues) {
    const document = data.documents?.[0];
    const link = data.link?.trim();
    if (isDocumentUploaded(document)) {
      return document;
    } else if (document) {
      return await createDocumentMutation.mutateAsync({ file: document.file });
    } else if (link) {
      if (link === fileDetails?.document?.url) {
        return fileDetails.document;
      } else {
        return await createDocumentMutation.mutateAsync({ link });
      }
    } else {
      throw new Error("Missing document/link");
    }
  }

  const error = fileDetailsError || parentFolderError;
  if (error) {
    return <ErrorPage error={error} />;
  }

  if (fileDetailsIsFetching || parentFolderIsPending) {
    return <FullSizeLoader withPadding />;
  }

  const defaultFormValues: DocumentFileFormValues = {
    link: fileDetails?.document === undefined || fileDetails.type !== "link" ? "" : fileDetails.document.url,
    name: fileDetails ? getOriginalTranslationValue(fileDetails.nameTranslations, fileDetails.originalLanguageId) : "",
    documents: fileDetails?.document === undefined || fileDetails.type === "link" ? [] : [fileDetails.document],
  };

  const formProps: DocumentFileFormProps = {
    folderName: parentFolder.name,
    folderId: parentFolder.id,
    defaultFormValues,
    originalLanguageId: fileDetails?.originalLanguageId,
    onSubmit: edit,
    isSubmitting: updateFileMutation.isPending || createDocumentMutation.isPending,
    isEditMode: true,
  };

  return <DocumentFileForm {...formProps} />;
}
